import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, Search, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatDate } from "@/lib/datetime";
import { PermBadge, EligibleBadge, STATUS, errMsg } from "@/components/email/EmParts";
import AddSubscriberDialog from "@/components/email/AddSubscriberDialog";

const FILTERS = [
  ["permission", "Permission", [["granted", "Consent granted"], ["soft_opt_in", "Soft opt-in"], ["unknown", "Unknown"], ["no_consent", "No consent"], ["withdrawn", "Withdrawn"]]],
  ["status", "Status", Object.entries(STATUS)],
  ["eligible", "Eligibility", [["yes", "Eligible"], ["no", "Not eligible"]]],
  ["linked", "Type", [["yes", "Customer"], ["no", "Standalone"]]],
];

function Row({ s }) {
  return (
    <tr className="border-t border-slate-100 hover:bg-slate-50" data-testid={`em-sub-row-${s.id}`}>
      <td className="py-2 pr-3"><Link to={`/email-marketing/subscribers/${s.id}`} className="font-medium text-slate-800 hover:text-indigo-700" data-testid={`em-sub-link-${s.id}`}>{`${s.first_name} ${s.last_name}`.trim() || "—"}</Link>
        <div className="text-xs text-slate-500 truncate max-w-[220px]">{s.email || "No email"}</div></td>
      <td className="pr-3"><PermBadge value={s.marketing_permission} /></td>
      <td className="pr-3 text-xs text-slate-600">{STATUS[s.subscription_status]}</td>
      <td className="pr-3"><EligibleBadge sub={s} /></td>
      <td className="pr-3 text-xs">{s.linked ? <Link to={`/users/${s.contact_legacy_id}`} className="text-indigo-600 hover:underline">#{s.contact_legacy_id}</Link> : <span className="text-slate-400">Standalone</span>}</td>
      <td className="pr-3 text-xs text-slate-500 hidden md:table-cell">{(s.tags || []).slice(0, 3).join(", ")}</td>
      <td className="text-xs text-slate-500 hidden md:table-cell">{formatDate(s.created_at)}</td>
    </tr>
  );
}

export default function EmSubscribers() {
  const { can } = useAuth();
  const manage = can("email_marketing.manage");
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(false);
  const page = Number(params.get("page") || 1);
  const load = useCallback(() => {
    const p = Object.fromEntries(params.entries());
    api.get("/email-marketing/subscribers", { params: { ...p, limit: 50 } }).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [params]);
  useEffect(() => { load(); }, [load]);
  const setParam = (k, v) => { const n = new URLSearchParams(params); if (v) n.set(k, v); else n.delete(k); if (k !== "page") n.delete("page"); setParams(n); };
  const pages = d ? Math.max(1, Math.ceil(d.total / d.limit)) : 1;
  return (
    <div className="space-y-4" data-testid="em-subscribers">
      <div className="flex flex-wrap items-center gap-2">
        <form className="relative flex-1 min-w-[200px] max-w-sm" onSubmit={(e) => { e.preventDefault(); setParam("q", q.trim()); }}>
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email or customer #" className="pl-9" data-testid="em-sub-search" />
        </form>
        {FILTERS.map(([k, label, opts]) => (
          <select key={k} value={params.get(k) || ""} onChange={(e) => setParam(k, e.target.value)} data-testid={`em-filter-${k}`}
            className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700">
            <option value="">{`${label}: all`}</option>
            {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        ))}
        {manage && <Button variant="outline" className="gap-1.5 ml-auto" asChild><Link to="/email-marketing/subscribers/mailcoach-import" data-testid="em-mailcoach-import-btn">Import from Mailcoach</Link></Button>}
        {manage && <Button className="gap-1.5" onClick={() => setOpen(true)} data-testid="em-add-subscriber-btn"><UserPlus className="w-4 h-4" />Add subscriber</Button>}
      </div>
      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        {!d ? <div className="p-6"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div> : (
          <table className="w-full text-sm" data-testid="em-sub-table">
            <thead><tr className="text-[11px] uppercase text-slate-400 text-left"><th className="py-2 pl-4 pr-3">Subscriber</th><th className="pr-3">Permission</th><th className="pr-3">Status</th><th className="pr-3">Send</th><th className="pr-3">Customer</th><th className="pr-3 hidden md:table-cell">Tags</th><th className="hidden md:table-cell">Added</th></tr></thead>
            <tbody className="[&_td:first-child]:pl-4">{d.items.map((s) => <Row key={s.id} s={s} />)}
              {!d.items.length && <tr><td colSpan={7} className="py-6 text-center text-slate-400" data-testid="em-sub-empty">No subscribers match these filters.</td></tr>}</tbody>
          </table>)}
      </div>
      {d && (
        <div className="flex items-center justify-between text-sm text-slate-500">
          <span data-testid="em-sub-total">{d.total.toLocaleString()} subscribers</span>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setParam("page", String(page - 1))} data-testid="em-sub-prev">Previous</Button>
            <span data-testid="em-sub-page">Page {page} of {pages}</span>
            <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setParam("page", String(page + 1))} data-testid="em-sub-next">Next</Button>
          </div>
        </div>)}
      <AddSubscriberDialog open={open} onOpenChange={setOpen} onCreated={load} />
    </div>
  );
}
